import { UnstyledButton } from '@cowbell/ui/core';
import { faChevronRight } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { ReasoningRow, ToolCallStatus } from '../../meta/types';
import { LiveText, motion, SmoothCollapse } from '../Motion';
import { formatDuration } from '../TurnStatus/turn-status';
import { useNow } from '../TurnStatus/useTurnClock';
import { groupSameToolRows, rowText, type RenderItem } from './group-rows';
import { settleRunningTools, splitThought } from './thinking-text';
import { useFoldForRoom, useIsOverflowing } from './fold-for-room';
import { splitThoughtSections } from './thought-sections';
import { ThoughtSections } from './ThoughtSections';
import styles from './ThinkingBlock.module.css';

export interface ThinkingBlockProps {
  readonly title: string;
  readonly rows: ReasoningRow[];
  readonly skillId?: string | null;
  readonly label?: string;
  readonly isComplete: boolean;
  readonly pendingReasoning?: string;
  /** The stream closed before this block completed. */
  readonly isInterrupted?: boolean;
  /** When the turn this block belongs to started. Used to time the block, so
   *  the duration survives the bubble remounting mid-turn. */
  readonly liveStartedAt?: number;
  /** Stable identity for this block across remounts, so a duration measured
   *  while streaming is still shown after the bubble re-renders from scratch. */
  readonly durationKey?: string;
  /** The answer has started arriving: thinking is over for the reader. */
  readonly isAnswering?: boolean;
}

type BlockState = 'live' | 'stopped' | 'done';

/** Elapsed time below this is not worth printing next to "Thinking". */
const SHOW_ELAPSED_AFTER_MS = 3_000;

/** Durations measured while streaming, kept past the component's lifetime:
 *  the streaming bubble can remount when the message finalizes, and the
 *  duration would otherwise be lost at exactly the moment it is shown. */
const measuredDurations = new Map<string, number>();

/**
 * The model's working, shown while it happens and folded away once it is
 * done — nobody goes back to read it afterwards.
 *
 * Live: "Thinking 12s" over a fixed-height window in which steps drop onto a
 * small timeline and the model's own reasoning streams beneath them. The
 * window is anchored to its bottom edge, so the newest line is always in
 * view and older ones fade out at the top; the page itself never grows.
 * The moment the answer starts, the window folds once into "Thought for 58s
 * · 8 steps". The full timeline stays one click away.
 */
export function ThinkingBlock({
  title,
  rows,
  label,
  isComplete,
  pendingReasoning,
  isInterrupted = false,
  liveStartedAt,
  durationKey,
  isAnswering = false,
}: ThinkingBlockProps) {
  const [isOpen, setIsOpen] = useState(false);
  // The answer starting is the end of thinking as far as the reader is
  // concerned, even if the server has not closed the block yet.
  const isSettled = isComplete || isAnswering;
  const state = blockState(isSettled, isInterrupted);
  const durationMs = useBlockDuration(isSettled, liveStartedAt, durationKey);
  const visibleRows = isInterrupted ? settleRunningTools(rows) : rows;

  // The fold waits for the answer's first frames to paint: a big first chunk
  // can hold the main thread for a moment, and a fold started inside that
  // moment finishes before the browser draws a single frame of it.
  const isFolding = useAfterPaint(state !== 'live');

  const toggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  return (
    <div className={styles.block} data-state={state}>
      <UnstyledButton
        className={styles.summary}
        onClick={toggle}
        aria-expanded={isOpen}
      >
        <SummaryLabel
          state={state}
          displayTitle={label ?? defaultLiveTitle(title)}
          durationMs={durationMs}
          stepCount={rows.length}
        />
        {state === 'live' && liveStartedAt !== undefined && (
          <LiveElapsed since={liveStartedAt} />
        )}
        <FontAwesomeIcon
          icon={faChevronRight}
          className={styles.chevron}
          rotation={isOpen ? 90 : undefined}
        />
      </UnstyledButton>

      <SmoothCollapse
        open={!isFolding && !isOpen}
        className={styles.hang}
        durationMs={FOLD_MS}
      >
        <LiveFeed rows={rows} pendingReasoning={pendingReasoning} />
      </SmoothCollapse>

      <SmoothCollapse open={isOpen} className={styles.hang}>
        <ol className={styles.timeline} data-testid="step-list">
          {groupSameToolRows(visibleRows).map((item, idx) => (
            <RenderRow item={item} key={`item-${idx}`} />
          ))}
        </ol>
        {pendingReasoning && (
          <div
            className={styles.thoughtBody}
            data-testid="pending-reasoning-tail"
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {pendingReasoning}
            </ReactMarkdown>
          </div>
        )}
      </SmoothCollapse>
    </div>
  );
}

/** How long the live chain takes to fold away once the answer starts. */
const FOLD_MS = 520;

/** Follows `value`, but turns true only after two painted frames: rAF does
 *  not fire while a long task holds the main thread, so whatever this gates
 *  starts once the page is drawing again. */
function useAfterPaint(value: boolean): boolean {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    if (!value) {
      setSettled(false);
      return;
    }
    let second = 0;
    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => setSettled(true));
    });
    return () => {
      cancelAnimationFrame(first);
      cancelAnimationFrame(second);
    };
  }, [value]);
  return settled;
}

/** One link of the live chain, as plain data: what it says, how it is
 *  doing, and the reasoning to show under it while it is the newest. */
interface ChainLinkData {
  readonly key: string;
  readonly label: string;
  readonly count: number;
  readonly status: ToolCallStatus;
  readonly thought: string;
}

/** The chain as it builds. Each position keeps one identity: the link the
 *  model is reasoning towards sits at the position its step will take, so
 *  when the step lands it is the same element and only its label changes.
 *  The newest link shows its reasoning; when the next one starts, that text
 *  folds into a one-line leg. */
function LiveFeed({
  rows,
  pendingReasoning,
}: Readonly<{ rows: ReasoningRow[]; pendingReasoning?: string }>) {
  const feedRef = useRef<HTMLDivElement>(null);
  const isOverflowing = useIsOverflowing(feedRef);
  const links = useLingeringLinks(chainLinks(rows, pendingReasoning));
  const newest = links.find((l) => l.isNewest && !l.isLeaving)?.link;
  const autoFolded = useFoldForRoom(
    feedRef,
    newest?.key ?? '',
    newest ? splitThoughtSections(newest.thought).length : 0
  );

  return (
    <div
      ref={feedRef}
      className={styles.liveFeed}
      data-overflowing={isOverflowing}
      data-testid="live-feed"
    >
      <ol className={styles.timeline}>
        {links.map(({ link, isNewest, isLeaving }) => (
          <ChainLink
            key={link.key}
            link={link}
            isNewest={isNewest}
            isLeaving={isLeaving}
            autoFolded={link.key === newest?.key ? autoFolded : 0}
          />
        ))}
      </ol>
    </div>
  );
}

function ChainLink({
  link,
  isNewest,
  isLeaving,
  autoFolded,
}: Readonly<{
  link: ChainLinkData;
  isNewest: boolean;
  isLeaving: boolean;
  autoFolded: number;
}>) {
  const status = isNewest && link.status === 'ok' ? 'running' : link.status;
  return (
    <li className={styles.step} data-leaving={isLeaving}>
      <StepDot status={status} />
      <div className={styles.stepBody}>
        <StepNote
          label={link.label}
          text={link.thought}
          isLive={isNewest && !isLeaving}
          autoFolded={autoFolded}
          bodyTestId="live-note-body"
        >
          <span
            key={link.label}
            className={`${styles.stepText} ${motion.tickIn}`}
          >
            {link.label}
          </span>
          {link.count > 1 && <CallCount count={link.count} />}
        </StepNote>
      </div>
    </li>
  );
}

/** "· 18 times" after a step the model ran repeatedly. */
function CallCount({ count }: Readonly<{ count: number }>) {
  return <span className={styles.callCount}>{`· ${count} times`}</span>;
}

/** The live chain as data: one link per step (repeated calls to a tool share
 *  one), plus a link for reasoning still arriving, at the position its step
 *  will take. */
function chainLinks(
  rows: ReasoningRow[],
  pendingReasoning: string | undefined
): ChainLinkData[] {
  const items = groupSameToolRows(rows);
  const links: ChainLinkData[] = items.map((item, idx) => ({
    key: `link-${idx}`,
    label: itemLabel(item),
    count: item.kind === 'group' ? item.rows.length : 1,
    status: itemStatus(item),
    thought: splitThought(itemReasoning(item)).body,
  }));
  const pending = pendingReasoning?.trim() ?? '';
  if (pending) {
    const { heading, body } = splitThought(pending);
    links.push({
      key: `link-${items.length}`,
      label: heading || 'Thinking',
      count: 1,
      status: 'running',
      thought: body,
    });
  }
  return links;
}

function itemLabel(item: RenderItem): string {
  if (item.kind === 'group') {
    return item.label;
  }
  if (item.row.kind === 'skill') {
    return `Used the ${humanizeSkill(item.row.skillName)} skill`;
  }
  return rowText(item.row);
}

function itemStatus(item: RenderItem): ToolCallStatus {
  if (item.kind === 'group') {
    return groupStatus(item.rows);
  }
  return item.row.kind === 'tool' ? item.row.status : 'ok';
}

function itemReasoning(item: RenderItem): string {
  const row = item.kind === 'group' ? item.rows.at(-1) : item.row;
  return row?.reasoning?.trim() ?? '';
}

interface PlacedLink {
  readonly link: ChainLinkData;
  readonly isNewest: boolean;
  readonly isLeaving: boolean;
}

/** Positions the links and lets a link that vanished without a successor —
 *  reasoning that joined an existing group instead of adding a step — fold
 *  away for FOLD_MS instead of disappearing. */
function useLingeringLinks(links: ChainLinkData[]): PlacedLink[] {
  const [leaving, setLeaving] = useState<ChainLinkData | null>(null);
  // The links array is rebuilt every render, so change is detected by the
  // set of keys; the previous render's links (for the departing link's last
  // label and text) come from a ref written after each commit.
  const signature = links.map((link) => link.key).join('|');
  const [seenSignature, setSeenSignature] = useState(signature);
  const lastLinks = useRef(links);

  if (signature !== seenSignature) {
    setSeenSignature(signature);
    const keys = new Set(links.map((link) => link.key));
    const gone = lastLinks.current.find((link) => !keys.has(link.key));
    if (gone) {
      setLeaving(gone);
    }
  }

  useLayoutEffect(() => {
    lastLinks.current = links;
  });

  useEffect(() => {
    if (!leaving) return;
    const timer = setTimeout(() => setLeaving(null), FOLD_MS);
    return () => clearTimeout(timer);
  }, [leaving]);

  const placed: PlacedLink[] = links.map((link, idx) => ({
    link,
    isNewest: idx === links.length - 1,
    isLeaving: false,
  }));
  if (leaving && !links.some((link) => link.key === leaving.key)) {
    placed.push({ link: leaving, isNewest: false, isLeaving: true });
  }
  return placed;
}

/** The model's words for the step in progress, in full. */
function ThoughtWindow({
  text,
  autoFolded,
}: Readonly<{ text: string; autoFolded?: number }>) {
  return <ThoughtSections text={text} autoFolded={autoFolded} />;
}

function blockState(isComplete: boolean, isInterrupted: boolean): BlockState {
  if (isInterrupted) {
    return 'stopped';
  }
  return isComplete ? 'done' : 'live';
}

/** The server titles the main block "Reasoning"; the chat calls the same
 *  thing "Thinking" everywhere else, so it reads as one continuous state from
 *  the moment the message is sent. Sub-asks carry their own label. */
function defaultLiveTitle(title: string): string {
  return title === 'Reasoning' || title.length === 0 ? 'Thinking' : title;
}

function SummaryLabel({
  state,
  displayTitle,
  durationMs,
  stepCount,
}: Readonly<{
  state: BlockState;
  displayTitle: string;
  durationMs: number | null;
  stepCount: number;
}>) {
  if (state === 'live') {
    return (
      <span className={styles.summaryText}>
        <LiveText>{displayTitle}</LiveText>
      </span>
    );
  }

  // The notice under the reply carries the warning; the header just says
  // where things got to.
  if (state === 'stopped') {
    return (
      <span className={styles.summaryText}>
        {stepCount > 0 ? `Stopped after ${stepLabel(stepCount)}` : 'Stopped'}
      </span>
    );
  }

  return (
    <span className={styles.summaryText}>
      {doneTitle(durationMs, stepCount)}
      {stepCount > 0 && durationMs !== null && (
        <span className={styles.summaryMeta}>
          {` · ${stepLabel(stepCount)}`}
        </span>
      )}
    </span>
  );
}

function doneTitle(durationMs: number | null, stepCount: number): string {
  if (durationMs !== null) {
    return `Thought for ${formatDuration(Math.max(durationMs, 1_000))}`;
  }
  if (stepCount > 0) {
    return `Worked through ${stepLabel(stepCount)}`;
  }
  return 'Thought briefly';
}

function stepLabel(count: number): string {
  return count === 1 ? '1 step' : `${count} steps`;
}

function LiveElapsed({ since }: Readonly<{ since: number }>) {
  const elapsedMs = useNow() - since;
  if (elapsedMs < SHOW_ELAPSED_AFTER_MS) {
    return null;
  }
  return (
    <span className={`${styles.summaryMeta} ${motion.fadeIn}`}>
      {formatDuration(elapsedMs)}
    </span>
  );
}

/** Times the block from the turn's start (or this mount) to completion.
 *  A block mounted already complete (history) has no duration. */
function useBlockDuration(
  isComplete: boolean,
  liveStartedAt: number | undefined,
  durationKey: string | undefined
): number | null {
  const [mountedAt] = useState(() => (isComplete ? null : Date.now()));
  const [durationMs, setDurationMs] = useState<number | null>(() =>
    durationKey ? measuredDurations.get(durationKey) ?? null : null
  );

  useEffect(() => {
    if (!isComplete || durationMs !== null) {
      return;
    }
    const start = liveStartedAt ?? mountedAt;
    if (start === null) {
      return;
    }
    const measured = Date.now() - start;
    if (durationKey) {
      measuredDurations.set(durationKey, measured);
    }
    setDurationMs(measured);
  }, [durationKey, durationMs, isComplete, liveStartedAt, mountedAt]);

  return durationMs;
}

function RenderRow({ item }: Readonly<{ item: RenderItem }>) {
  if (item.kind === 'group') {
    return <ToolGroupRow group={item} />;
  }
  return <RowContent row={item.row} />;
}

/** Collapsed, counted parent for a run of consecutive calls to the same tool.
 *  Starts collapsed; expands to show each call's distinct line (OMNI-449). */
function ToolGroupRow({
  group,
}: Readonly<{ group: Extract<RenderItem, { kind: 'group' }> }>) {
  const [isOpen, setIsOpen] = useState(false);
  const toggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const count = group.rows.length;
  const { label } = group;

  return (
    <li className={styles.step}>
      <StepDot status={groupStatus(group.rows)} />
      <div className={styles.stepBody}>
        <UnstyledButton
          className={styles.stepToggle}
          onClick={toggle}
          aria-expanded={isOpen}
          aria-label={
            isOpen
              ? `Hide ${count} steps for ${label}`
              : `Show ${count} steps for ${label}`
          }
          data-testid="tool-group"
        >
          <span className={styles.stepText}>{label}</span>
          <CallCount count={count} />
          <FontAwesomeIcon
            icon={faChevronRight}
            className={styles.rowChevron}
            rotation={isOpen ? 90 : undefined}
          />
        </UnstyledButton>
        <SmoothCollapse open={isOpen}>
          <ul className={styles.subSteps} data-testid="tool-group-children">
            {group.rows.map((row, idx) => (
              <li
                className={styles.subStep}
                key={`grp-row-${row.stepIndex}-${idx}`}
              >
                {rowText(row)}
              </li>
            ))}
          </ul>
        </SmoothCollapse>
      </div>
    </li>
  );
}

function groupStatus(rows: readonly ReasoningRow[]): ToolCallStatus {
  let running = false;
  for (const row of rows) {
    if (row.kind !== 'tool') {
      continue;
    }
    if (row.status === 'error') {
      return 'error';
    }
    if (row.status === 'running') {
      running = true;
    }
  }
  return running ? 'running' : 'ok';
}

function RowContent({ row }: Readonly<{ row: ReasoningRow }>) {
  const label = rowLabel(row);
  const status = row.kind === 'tool' ? row.status : 'ok';
  const note = row.reasoning?.trim() ?? '';
  return (
    <li className={styles.step}>
      <StepDot status={status} />
      <div className={styles.stepBody}>
        <StepNote label={label} text={splitThought(note).body || note}>
          <span className={styles.stepText}>{label}</span>
        </StepNote>
      </div>
    </li>
  );
}

function rowLabel(row: ReasoningRow): string {
  if (row.kind === 'tool') {
    return row.displayName;
  }
  if (row.kind === 'skill') {
    return `Used the ${humanizeSkill(row.skillName)} skill`;
  }
  return row.text;
}

/**
 * A step's label, and the deep-thinking note under it when there is one.
 * With a note, the label becomes a toggle carrying an always-visible chevron:
 * ">" folded, pointing down open. The live step's note is open; when the
 * chain moves on it folds closed and the chevron turns in the same motion,
 * so the reader sees where it went. Without a note it is just the label.
 */
function StepNote({
  label,
  text,
  isLive = false,
  autoFolded = 0,
  bodyTestId = 'row-reasoning-body',
  children,
}: Readonly<{
  label: string;
  text: string;
  isLive?: boolean;
  /** Parts of the live note folded to make room (see useFoldForRoom). */
  autoFolded?: number;
  bodyTestId?: string;
  children: ReactNode;
}>) {
  const [isOpen, setIsOpen] = useState(false);
  if (text.length === 0) {
    return <span className={styles.stepLabel}>{children}</span>;
  }
  const showFull = isLive || isOpen;

  return (
    <>
      <UnstyledButton
        className={`${styles.stepLabel} ${styles.stepToggle}`}
        onClick={() => setIsOpen(!showFull)}
        aria-expanded={showFull}
        aria-label={
          showFull
            ? `Hide reasoning for ${label}`
            : `Show reasoning for ${label}`
        }
      >
        {children}
        <FontAwesomeIcon
          icon={faChevronRight}
          className={styles.rowChevron}
          rotation={showFull ? 90 : undefined}
        />
      </UnstyledButton>
      <SmoothCollapse open={showFull} durationMs={FOLD_MS}>
        <div data-testid={bodyTestId}>
          <ThoughtWindow text={text} autoFolded={autoFolded} />
        </div>
      </SmoothCollapse>
    </>
  );
}

/** `portfolio-summary` → `portfolio summary`. */
function humanizeSkill(skillName: string): string {
  return skillName.replaceAll(/[-_]+/g, ' ').trim();
}

/** The timeline marker for one step. Running pulses; done is a quiet filled
 *  dot; failed is red. Labelled so assistive tech hears the outcome. */
function StepDot({ status }: Readonly<{ status: ToolCallStatus }>) {
  if (status === 'running') {
    return (
      <span
        className={styles.dot}
        data-status="running"
        data-testid="tool-running"
        aria-label="running"
      />
    );
  }
  return (
    <span
      className={styles.dot}
      data-status={status}
      aria-label={status === 'error' ? 'error' : 'ok'}
    />
  );
}
